import { LitElement, html, property, customElement, TemplateResult, internalProperty } from "lit-element";
import { classMap } from "lit-html/directives/class-map.js";
import { unsafeHTML } from "lit-html/directives/unsafe-html.js";
import { v4 as uuidv4 } from "uuid";
import parseISO from "date-fns/parseISO";
import isValid from "date-fns/isValid";
import formatISO from "date-fns/formatISO";
import format from "date-fns/format";
import de from "date-fns/locale/de";
import imageObserver from "../lazy-image-loading";
import { isNullOrWhitespace } from "../util/isNullOrWhitespace";

@customElement("teaser-item")
export class TeaserItem extends LitElement {
  // true if teaser shall flip content and image on hover
  @property({ type: Boolean }) public isFlippable!: boolean;
  // true if teaser shall display title and description text
  @property({ type: Boolean }) public showTitleAndDescription!: boolean;
  // true if teaser shall display a line below the image
  @property({ type: Boolean }) public showBottomLine!: boolean;
  @property({ type: Boolean }) public lazyImage!: boolean;
  // content properties
  @property({ type: String }) public title = "";
  @property({ type: String }) public description = "";
  @property({ type: String }) public frontImageUrl = "";
  @property({ type: String }) public backImageUrl = "";
  @property({ type: String }) public frontHeadline = "";
  @property({ type: String }) public backHeadline = "";
  @property({ type: String }) public frontBackgroundColor = "";
  @property({ type: String }) public backBackgroundColor = "";
  @property({ type: String }) public linkUrl = "";
  @property({ type: String }) public linkTarget = "";
  @property({ type: String }) public dateString: string | null = null;

  @internalProperty()
  private _isFlipped = false;

  public get isFlipped(): boolean {
    return this._isFlipped;
  }

  public readonly id: string;

  public constructor() {
    super();
    this.id = uuidv4();
  }

  public doTheFlip(): void {
    if (!this.isFlippable || this.isFlipped) {
      return;
    }
    this._isFlipped = true;
  }

  private get hasLink(): boolean {
    return !isNullOrWhitespace(this.linkUrl);
  }

  private get date(): Date | null {
    if (this.dateString == null) {
      return null;
    }
    const parsedDate = parseISO(this.dateString);
    return isValid(parsedDate) ? parsedDate : null;
  }

  // disable shadow DOM
  protected createRenderRoot(): this {
    return this;
  }

  protected updated(): void {
    imageObserver.observe();
  }

  protected render(): TemplateResult | "" {
    if (this.hasLink) {
      this.classList.add("teaser-item--clickable");
    } else {
      this.classList.remove("teaser-item--clickable");
    }
    if (this.isFlippable && (!isNullOrWhitespace(this.backImageUrl) || !isNullOrWhitespace(this.backBackgroundColor))) {
      this.classList.add("teaser-item--flippable");
    } else {
      this.classList.remove("teaser-item--flippable");
    }

    const frontBackgroundStyle =
      this.frontBackgroundColor !== "" ? `background-color: ${this.frontBackgroundColor}` : "";
    const backBackgroundStyle = this.backBackgroundColor !== "" ? `background-color: ${this.backBackgroundColor}` : "";
    const frontImageElement =
      this.frontImageUrl !== ""
        ? html`<img
            data-src="${this.lazyImage ? this.frontImageUrl : ""}"
            src="${this.lazyImage ? "" : this.frontImageUrl}"
            alt=""
            role="presentation"
            class=${classMap({ lozad: this.lazyImage })}
          />`
        : "";
    const backImageElement =
      this.backImageUrl !== ""
        ? html`<img
            data-src="${this.lazyImage ? this.backImageUrl : ""}"
            src="${this.lazyImage ? "" : this.backImageUrl}"
            alt=""
            role="presentation"
            class=${classMap({ lozad: this.lazyImage })}
          />`
        : "";

    const separatorElement = this.showBottomLine ? html`<div class="card-separator"></div>` : "";
    const dateElement =
      this.date === null
        ? ""
        : html`<time datetime="${formatISO(this.date, { representation: "date", format: "extended" })}"
            >${format(this.date, "d. MMMM y", { locale: de })}</time
          >`;
    const contentElements = this.showTitleAndDescription
      ? html`<div class="card-body">
          <h1 class="card-title">${this.title}</h1>
          <div class="card-date">${dateElement}</div>
          <p class="card-text">${unsafeHTML(this.description)}</p>
        </div>`
      : "";
    const articleElement = html`<article class="card">
      <div class="card-img">
        <div class="card-img-inner ${classMap({ "card-img-inner--flipped": this.isFlipped })}">
          <div class="card-img-front" style="${frontBackgroundStyle}">
            ${frontImageElement}
            <div class="card-img-overlay">
              <span class="card-title">${this.frontHeadline}</span>
            </div>
          </div>
          <div class="card-img-back" style="${backBackgroundStyle}">
            ${backImageElement}
            <div class="card-img-overlay">
              <span class="card-title">${this.backHeadline}</span>
            </div>
          </div>
        </div>
      </div>

      ${separatorElement} ${contentElements}
    </article>`;

    return this.hasLink
      ? html`<a href="${this.linkUrl}" target="${this.linkTarget}"> ${articleElement} </a>`
      : html`${articleElement}`;
  }
}
